import { useEffect, useState } from 'react';

import {
  ApiRef,
  BackstageIdentityApi,
  IdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  useApi,
} from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import '../../design-system/aranox-login.css';

const ASSET_BASE =
  '/brand-assets';

type AranoxAuthApi = OpenIdConnectApi &
  ProfileInfoApi &
  BackstageIdentityApi &
  SessionApi;

interface AranoxProvider {
  id: string;
  title: string;
  message: string;
  apiRef: ApiRef<AranoxAuthApi>;
}

export interface AranoxSignInPageProps {
  provider: AranoxProvider;
  onSignInSuccess: (identityApi: IdentityApi) => void;
}

interface HeadMetadata {
  selector: string;
  tag: 'meta' | 'link';
  attributes: Record<string, string>;
}

const browserMetadata: HeadMetadata[] = [
  {
    selector: 'link[rel="canonical"]',
    tag: 'link',
    attributes: {
      rel: 'canonical',
      href: 'https://portal.aranox.com.br/',
    },
  },
  {
    selector: 'link[rel="icon"][sizes="any"]',
    tag: 'link',
    attributes: {
      rel: 'icon',
      href: `${ASSET_BASE}/aranox-icone/aranox_favicon.ico`,
      sizes: 'any',
    },
  },
  {
    selector: 'link[rel="icon"][sizes="16x16"]',
    tag: 'link',
    attributes: {
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      href: `${ASSET_BASE}/aranox-icone/aranox_favicon-16x16.png`,
    },
  },
  {
    selector: 'link[rel="icon"][sizes="32x32"]',
    tag: 'link',
    attributes: {
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      href: `${ASSET_BASE}/aranox-icone/aranox_favicon-32x32.png`,
    },
  },
  {
    selector: 'link[rel="icon"][sizes="48x48"]',
    tag: 'link',
    attributes: {
      rel: 'icon',
      type: 'image/png',
      sizes: '48x48',
      href: `${ASSET_BASE}/aranox-icone/aranox_favicon-48x48.png`,
    },
  },
  {
    selector: 'link[rel="apple-touch-icon"]',
    tag: 'link',
    attributes: {
      rel: 'apple-touch-icon',
      sizes: '180x180',
      href: `${ASSET_BASE}/aranox-icone/aranox_apple-touch-icon.png`,
    },
  },
  {
    selector: 'link[rel="manifest"]',
    tag: 'link',
    attributes: {
      rel: 'manifest',
      href: `${ASSET_BASE}/aranox-icone/aranox_site.webmanifest`,
    },
  },
  {
    selector: 'meta[name="description"]',
    tag: 'meta',
    attributes: {
      name: 'description',
      content:
        'Portal ARANOX — inteligência, conexão e inovação corporativa no ecossistema ARANOX.',
    },
  },
  {
    selector: 'meta[name="theme-color"]',
    tag: 'meta',
    attributes: {
      name: 'theme-color',
      content: '#061329',
    },
  },
  {
    selector: 'meta[name="msapplication-TileColor"]',
    tag: 'meta',
    attributes: {
      name: 'msapplication-TileColor',
      content: '#061329',
    },
  },
  {
    selector: 'meta[property="og:title"]',
    tag: 'meta',
    attributes: {
      property: 'og:title',
      content: 'Portal ARANOX | ARANOX Digital Platform',
    },
  },
  {
    selector: 'meta[property="og:description"]',
    tag: 'meta',
    attributes: {
      property: 'og:description',
      content:
        'Inteligência, conexão e inovação corporativa no ecossistema ARANOX.',
    },
  },
  {
    selector: 'meta[property="og:type"]',
    tag: 'meta',
    attributes: {
      property: 'og:type',
      content: 'website',
    },
  },
  {
    selector: 'meta[property="og:url"]',
    tag: 'meta',
    attributes: {
      property: 'og:url',
      content: 'https://portal.aranox.com.br/',
    },
  },
  {
    selector: 'meta[property="og:site_name"]',
    tag: 'meta',
    attributes: {
      property: 'og:site_name',
      content: 'ARANOX',
    },
  },
  {
    selector: 'meta[property="og:locale"]',
    tag: 'meta',
    attributes: {
      property: 'og:locale',
      content: 'pt_BR',
    },
  },
  {
    selector: 'meta[property="og:image"]',
    tag: 'meta',
    attributes: {
      property: 'og:image',
      content: `${ASSET_BASE}/social/aranox-og.jpg`,
    },
  },
  {
    selector: 'meta[property="og:image:secure_url"]',
    tag: 'meta',
    attributes: {
      property: 'og:image:secure_url',
      content: `${ASSET_BASE}/social/aranox-og.jpg`,
    },
  },
  {
    selector: 'meta[property="og:image:width"]',
    tag: 'meta',
    attributes: {
      property: 'og:image:width',
      content: '1200',
    },
  },
  {
    selector: 'meta[property="og:image:height"]',
    tag: 'meta',
    attributes: {
      property: 'og:image:height',
      content: '630',
    },
  },
  {
    selector: 'meta[property="og:image:type"]',
    tag: 'meta',
    attributes: {
      property: 'og:image:type',
      content: 'image/jpeg',
    },
  },
  {
    selector: 'meta[property="og:image:alt"]',
    tag: 'meta',
    attributes: {
      property: 'og:image:alt',
      content: 'ARANOX — Inteligência, Conexão e Inovação que Transformam do ecossistema ARANOX',
    },
  },
  {
    selector: 'meta[name="twitter:card"]',
    tag: 'meta',
    attributes: {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
  },
  {
    selector: 'meta[name="twitter:title"]',
    tag: 'meta',
    attributes: {
      name: 'twitter:title',
      content: 'Portal ARANOX | ARANOX Digital Platform',
    },
  },
  {
    selector: 'meta[name="twitter:description"]',
    tag: 'meta',
    attributes: {
      name: 'twitter:description',
      content:
        'Inteligência, conexão e inovação corporativa.',
    },
  },
  {
    selector: 'meta[name="twitter:image"]',
    tag: 'meta',
    attributes: {
      name: 'twitter:image',
      content: `${ASSET_BASE}/social/aranox-og.jpg`,
    },
  },
];


function applyMetadata() {
  document.title = 'Portal ARANOX | ARANOX Digital Platform';
  document.documentElement.lang = 'pt-BR';

  browserMetadata.forEach(item => {
    let element = document.head.querySelector<HTMLElement>(item.selector);

    if (!element) {
      element = document.createElement(item.tag);
      document.head.appendChild(element);
    }

    Object.entries(item.attributes).forEach(([name, value]) => {
      element?.setAttribute(name, value);
    });
  });
}

export function AranoxSignInPage({
  provider,
  onSignInSuccess,
}: AranoxSignInPageProps) {
  const authApi = useApi(provider.apiRef);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    applyMetadata();
  }, []);

  const handleSignIn = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const identityResponse = await authApi.getBackstageIdentity({
        instantPopup: true,
      });

      if (!identityResponse) {
        throw new Error(
          'O provedor corporativo não está configurado para autenticação.',
        );
      }

      const profile = await authApi.getProfile();

      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
    } catch (error) {
      const normalizedError =
        error instanceof Error
          ? error
          : new Error('Não foi possível iniciar a autenticação.');

      if (normalizedError.name !== 'PopupRejectedError') {
        setErrorMessage(normalizedError.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="aranox-login">
      <div className="aranox-login__ambient" aria-hidden="true">
        <span className="aranox-login__orb aranox-login__orb--one" />
        <span className="aranox-login__orb aranox-login__orb--two" />
        <span className="aranox-login__grid" />
        <span className="aranox-login__wave aranox-login__wave--one" />
        <span className="aranox-login__wave aranox-login__wave--two" />
      </div>

      <section className="aranox-login__content">
        <div className="aranox-login__brand">
          <p className="aranox-login__eyebrow">ARANOX DIGITAL PLATFORM</p>

          <img
            className="aranox-login__brand-logo"
            src={`${ASSET_BASE}/aranox-logo-header.webp`}
            alt="ARANOX"
            width="909"
            height="691"
            decoding="async"
            fetchPriority="high"
          />

          <div className="aranox-login__brand-message">
            <h1>Inteligência, Conexão e Inovação que Transformam</h1>
            <span className="aranox-login__accent-line" aria-hidden="true" />
            <p>
              Plataforma integrada para gestão inteligente de ativos, processos e desempenho em tempo real. Tecnologia e dados trabalhando para decisões melhores
              aos processos do ecossistema <strong>ARANOX</strong>.
            </p>
          </div>
        </div>

        <section
          className="aranox-login__card"
          aria-labelledby="aranox-access-title"
        >
          <div className="aranox-login__security">
            <span className="aranox-login__security-icon" aria-hidden="true">
              ✓
            </span>
            Ambiente corporativo seguro
          </div>

          <div className="aranox-login__card-heading">
            <p>Bem-vindo</p>
            <h2 id="aranox-access-title">Acesse o Portal ARANOX</h2>
            <span>
              Use sua identidade corporativa ARANOX para continuar.
            </span>
          </div>

          <img
            className="aranox-login__card-logo"
            src={`${ASSET_BASE}/aranox-logo-header.webp`}
            alt=""
            width="512"
            height="512"
            decoding="async"
          />

          <button
            className="aranox-login__button"
            type="button"
            onClick={handleSignIn}
            disabled={loading}
            aria-busy={loading}
          >
            <span className="aranox-login__button-lock" aria-hidden="true">
              {loading ? '•••' : '↗'}
            </span>
            <span>
              {loading ? 'CONECTANDO...' : 'ENTRAR NO PORTAL ARANOX'}
            </span>
          </button>

          {errorMessage && (
            <div className="aranox-login__error" role="alert">
              {errorMessage}
            </div>
          )}

          <div className="aranox-login__privacy">
            <span aria-hidden="true">◆</span>
            Acesso corporativo protegido
          </div>
        </section>
      </section>

      <section
        className="aranox-login__portals"
        aria-label="Portais do ecossistema ARANOX"
      >
        <a className="aranox-login__portal-card aranox-login__portal-card--vitalis" href="https://vitalis.aranox.com.br/">
          <img src={`${ASSET_BASE}/vitalis_logo_branco_transp_v2.png`} alt="VITALIS" width="512" height="512" loading="lazy" decoding="async" />
          <p>Inteligência e dados para melhores decisões.</p>
          <span>ACESSAR O PAINEL</span>
        </a>

        <a className="aranox-login__portal-card aranox-login__portal-card--cyrion" href="https://cyrion.aranox.com.br/">
          <img src={`${ASSET_BASE}/cyrion_logo_branco_transp_crop.png`} alt="CYRION" width="909" height="691" loading="lazy" decoding="async" />
          <p>Gestão inteligente para alta performance.</p>
          <span>ACESSAR O PAINEL</span>
        </a>

        <a className="aranox-login__portal-card aranox-login__portal-card--cyrius" href="https://cyrius.aranox.com.br/">
          <img src={`${ASSET_BASE}/cyrius_logo_branco_transp.png`} alt="CYRIUS" width="512" height="512" loading="lazy" decoding="async" />
          <p>Conectividade e automação para operações inteligentes.</p>
          <span>ACESSAR O PAINEL</span>
        </a>
      </section>
    </main>
  );
}
